import React from 'react';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { copyToClipboard } from '../../../../helpers';
import { useAppDispatch } from '../../../hooks';
import { pushMessage } from '../../../../features/global-messages/global-messages-slice';
import { downloadAccountingWorkbook } from './accounting-excel-export';
import {
    AccountingWorkSheets,
    pendingClipboardText,
    PendingDocument,
} from './accounting-export-rules';

// One entry point for everything the user does with the month once it is
// reviewed: download it, or chase the documents that are still missing
// paperwork. Sales and expenses copy separately because they are chased with
// different people.
export function AccountingActionsMenu({
    exportWorksheets,
    pendingBankSales,
    pendingBankExpenses,
    pendingSales,
    pendingExpenses,
    disabled,
    monthLabel,
}: {
    exportWorksheets: AccountingWorkSheets;
    pendingBankSales: PendingDocument[];
    pendingBankExpenses: PendingDocument[];
    pendingSales: PendingDocument[];
    pendingExpenses: PendingDocument[];
    disabled: boolean;
    monthLabel: string;
}) {
    const dispatch = useAppDispatch();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const close = () => setAnchorEl(null);

    const handleCopy = async (
        kindLabel: string,
        documents: PendingDocument[],
    ) => {
        close();
        const ok = await copyToClipboard(
            pendingClipboardText(kindLabel, monthLabel, documents),
        );
        dispatch(
            pushMessage({
                message: ok
                    ? `Pendientes de ${kindLabel} copiados (${documents.length})`
                    : 'No se pudo copiar',
                options: { variant: ok ? 'success' : 'error' },
            }),
        );
    };

    const handleExport = async () => {
        close();
        await downloadAccountingWorkbook({
            worksheets: exportWorksheets,
            monthLabel,
            pendingBankSales,
            pendingBankExpenses,
            pendingSales,
            pendingExpenses,
        });
    };

    return (
        <>
            <Button
                variant={'outlined'}
                size={'small'}
                disabled={disabled}
                endIcon={<ArrowDropDownIcon />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                Acciones
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handleExport}>
                    <ListItemIcon>
                        <DownloadIcon fontSize={'small'} />
                    </ListItemIcon>
                    <ListItemText>Exportar Excel</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={pendingBankSales.length === 0}
                    onClick={() =>
                        void handleCopy('bancos / ventas', pendingBankSales)
                    }
                >
                    <ListItemIcon>
                        <ContentCopyIcon fontSize={'small'} />
                    </ListItemIcon>
                    <ListItemText>
                        {`Copiar pendientes bancarios de ventas (${pendingBankSales.length})`}
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={pendingBankExpenses.length === 0}
                    onClick={() =>
                        void handleCopy('bancos / gastos', pendingBankExpenses)
                    }
                >
                    <ListItemIcon>
                        <ContentCopyIcon fontSize={'small'} />
                    </ListItemIcon>
                    <ListItemText>
                        {`Copiar pendientes bancarios de gastos (${pendingBankExpenses.length})`}
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={pendingSales.length === 0}
                    onClick={() => void handleCopy('ventas', pendingSales)}
                >
                    <ListItemIcon>
                        <ContentCopyIcon fontSize={'small'} />
                    </ListItemIcon>
                    <ListItemText>
                        {`Copiar pendientes de ventas (${pendingSales.length})`}
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={pendingExpenses.length === 0}
                    onClick={() => void handleCopy('gastos', pendingExpenses)}
                >
                    <ListItemIcon>
                        <ContentCopyIcon fontSize={'small'} />
                    </ListItemIcon>
                    <ListItemText>
                        {`Copiar pendientes de gastos (${pendingExpenses.length})`}
                    </ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
